import { LoadingButton, Stack } from "@altimateai/lego";
import { Card, CardContent, CardFooter, Alert, Textarea } from "@altimateai/lego";
import { z } from "zod";
import { ApiHelper, useMutation } from "../api/index";
import { CoachAiConfirmationResponse, CoachAiResponse } from "./types";
import { useState } from "react";
import CoachFormInput from "./CoachFormInput";
import { CoachFormProps } from "./CoachForm";
import { Button } from "@altimateai/lego";

export const coachFormSchema = z.object({
  content: z
    .string()
    .min(10, { message: "Feedback must be at least 10 characters" })
    .max(500, { message: "Feedback must not exceed 500 characters" })
    .min(1, { message: "Feedback is required" }),
});

export type CoachFormValues = z.infer<typeof coachFormSchema>;

const AddLearningForm = ({ taskLabel, context, onClose, extra = {} }: CoachFormProps) => {
  const [coachAiResponse, setCoachAiResponse] = useState<CoachAiResponse>();
  const [aiLearningEditable, setAiLearningEditable] = useState(false);

  const onSuccess = (data: CoachAiResponse) => {
    setCoachAiResponse(data);
  };

  const makeAiLearningEditable = () => {
    setAiLearningEditable(true);
  };

  const makeAiLearningNonEditable = () => {
    setAiLearningEditable(false);
  };

  const saveAiLearning = async (): Promise<CoachAiConfirmationResponse> => {
    if (!coachAiResponse) throw new Error("No AI response to save");
    makeAiLearningNonEditable();
    return await ApiHelper.post<CoachAiConfirmationResponse>(`coach/training/confirm`, {
      content: coachAiResponse.ai_response,
      category: coachAiResponse.category,
      taskLabel: taskLabel,
      personalizationScope: coachAiResponse.personalizationScope,
      metadata: context,
      ...extra,
    });
  };

  const {
    mutate,
    error,
    loading: isSaving,
    data,
  } = useMutation<void, CoachAiConfirmationResponse>({
    queryFn: saveAiLearning,
    onSuccess: () => {
      setAiLearningEditable(false);
    },
  });

  const handleSave = () => {
    mutate();
  };

  if (data?.frontend_url) {
    return (
      <Alert>
        Thank you for coaching Datapilot. Click the link to{" "}
        <a
          className="al-underline al-font-medium"
          href={`${data.frontend_url}/teammates/${taskLabel}?learning=${data.train_doc_uid}`}
        >
          View learnings
        </a>
      </Alert>
    );
  }

  return (
    <Stack direction="column">
      <p className="m-0">
        Here, you can provide instructions in natural language including your terminology, rules or
        conventions to improve the quality of responses
      </p>
      <CoachFormInput taskLabel={taskLabel} onSuccess={onSuccess} onCancel={onClose} />
      {coachAiResponse && (
        <Card className="mt-4">
          <CardContent className="al-pt-4">
            <p>Below are the learnings by AI based on the entered instructions:</p>
            <div>
              {aiLearningEditable ? (
                <Textarea
                  value={coachAiResponse.ai_response}
                  onChange={e => {
                    setCoachAiResponse({
                      ...coachAiResponse,
                      ai_response: e.target.value,
                    });
                  }}
                  className="al-min-h-[100px]"
                />
              ) : (
                coachAiResponse.ai_response
              )}
            </div>
            {error && <div className="al-mt-4 al-text-destructive">{error.message}</div>}
          </CardContent>
          <CardFooter className="border-0">
            <Stack direction="row" justify="end" gap="md">
              {!aiLearningEditable ? (
                <Button variant="secondary" onClick={makeAiLearningEditable}>
                  Edit
                </Button>
              ) : (
                <Button variant="outline" onClick={makeAiLearningNonEditable}>
                  Cancel
                </Button>
              )}
              <LoadingButton loading={isSaving} onClick={handleSave} color="primary">
                Save
              </LoadingButton>
            </Stack>
          </CardFooter>
        </Card>
      )}
    </Stack>
  );
};

export default AddLearningForm;
